import * as React from 'react'

import { FILTER_TYPES } from 'modules/core/components/DataTable/constants'
import useFilters from 'modules/core/components/DataTable/hooks/useFilters'
import type { Filter as FilterType } from 'modules/core/components/DataTable/types'

import BooleanFilter from './Boolean'
import DateFilter from './Date'
import EntityFilter from './Entity'
import IntegerFilter from './Integer'
import StringFilter from './String'

type Props<T> = {
  filter: FilterType<T>
  defaultOpen?: boolean
}

const isEmptyValue = (value: unknown) => value === null || value === undefined

const isEmpty = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.every(isEmptyValue)
  }

  return isEmptyValue(value)
}

export default function FilterSwitch<T>({ filter: dataTableFilter, defaultOpen }: Props<T>) {
  const { onRemoveFilter, onUpdateFilter } = useFilters<T>()
  const { filter } = dataTableFilter

  const onCategoryChange = (category: FilterType<T>['category']) => {
    onUpdateFilter({
      ...dataTableFilter,
      value: category.value,
      category,
    })
  }

  const onValueChange = (value: unknown) => {
    if (filter.default || !isEmpty(value)) {
      onUpdateFilter({
        ...dataTableFilter,
        value,
      })
      return
    }

    onRemoveFilter(dataTableFilter)
  }

  switch (filter.type) {
    case FILTER_TYPES.STRING:
      return (
        <StringFilter
          filter={dataTableFilter}
          onValueChange={onValueChange}
          onCategoryChange={onCategoryChange}
          defaultOpen={defaultOpen}
        />
      )
    case FILTER_TYPES.INTEGER:
      return (
        <IntegerFilter
          filter={dataTableFilter}
          onValueChange={onValueChange}
          onCategoryChange={onCategoryChange}
          defaultOpen={defaultOpen}
        />
      )
    case FILTER_TYPES.DATE:
      return (
        <DateFilter
          filter={dataTableFilter}
          onValueChange={onValueChange}
          onCategoryChange={onCategoryChange}
          defaultOpen={defaultOpen}
        />
      )
    case FILTER_TYPES.BOOLEAN:
      return (
        <BooleanFilter
          filter={dataTableFilter}
          onValueChange={onValueChange}
          onCategoryChange={onCategoryChange}
          defaultOpen={defaultOpen}
        />
      )
    case FILTER_TYPES.ENTITY:
      return (
        <EntityFilter
          filter={dataTableFilter}
          onValueChange={onValueChange}
          onCategoryChange={onCategoryChange}
          defaultOpen={defaultOpen}
        />
      )
  }
}
